export type WorkflowStatus =
  | 'DRAFT'
  | 'ACTIVE'
  | 'PAUSED'
  | 'ARCHIVED';

export type WorkflowObjectType =
  | 'CONTACT'
  | 'COMPANY'
  | 'DEAL'
  | 'QUOTE'
  | 'CUSTOM_OBJECT'
  | 'SUBSCRIPTION'
  | 'TICKET';

export interface WorkflowResponse {
  id: number;
  intIdBussines: number;
  idFolder?: number | null;
  name: string;
  description?: string | null;
  objectType: WorkflowObjectType;
  status: WorkflowStatus;
  idOwnerUser?: number | null;
  isTemplate: boolean;
  templateCategory?: string | null;
  tags: string[];
  settings: Record<string, any>;
  publishedVersionId?: number | null;
  reEnrollmentEnabled: boolean;
  maxActiveEnrollments?: number | null;
  priorityOrder: number;
  activatedAt?: string | null;
  pausedAt?: string | null;
  archivedAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  datetimeCreated: string;
  datetimeUpdated: string;
}

export interface WorkflowCreateRequest {
  intIdBussines: number;
  idFolder?: number | null;
  name: string;
  description?: string | null;
  objectType: WorkflowObjectType;
  idOwnerUser?: number | null;
  tags?: string[];
  settings?: Record<string, any>;
  priorityOrder?: number;
  maxActiveEnrollments?: number | null;
}

export interface WorkflowUpdateRequest {
  idFolder?: number | null;
  name?: string;
  description?: string | null;
  /**
   * OBLIGATORIO aunque el tipo lo marque opcional: `WorkflowUpdateRequest.objectType`
   * es `@NotBlank` en el backend. Omitirlo devolvía 400 "objectType: El objectType es
   * obligatorio" en CADA edición, con el tipo de objeto visiblemente seleccionado en
   * pantalla (INC_CRM_020 (2) inc10).
   */
  objectType?: WorkflowObjectType;
  idOwnerUser?: number | null;
  tags?: string[];
  settings?: Record<string, any>;
  priorityOrder?: number;
  maxActiveEnrollments?: number | null;
  /**
   * INC_CRM_020 (2) inc9 — la actualización es PARCIAL: mandar un campo en `null` significa
   * "no lo toques", no "bórralo". Para DESASIGNAR hay que nombrarlo aquí. Sin esto no había
   * forma de sacar un workflow de su carpeta, y por tanto tampoco de vaciar una carpeta para
   * poder eliminarla. Válidos: idFolder, idOwnerUser, maxActiveEnrollments, description.
   */
  clearFields?: string[];
}

export interface WorkflowFilterRequest {
  businessId: number;
  page?: number;
  size?: number;
  status?: WorkflowStatus;
  objectType?: WorkflowObjectType;
  folderId?: number;
  ownerId?: number;
  searchTerm?: string;
}

export interface WorkflowDuplicateRequest {
  name: string;
}

export interface WorkflowTemplateCreateRequest {
  name: string;
  description?: string;
  templateCategory?: string;
}

export interface WorkflowFolderResponse {
  id: number;
  intIdBussines: number;
  parentFolderId?: number | null;
  name: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdBy?: number | null;
  updatedBy?: number | null;
  datetimeCreated: string;
  datetimeUpdated: string;
}

export interface WorkflowFolderCreateRequest {
  intIdBussines: number;
  parentFolderId?: number | null;
  name: string;
  description?: string | null;
  sortOrder?: number;
}

export interface WorkflowFolderUpdateRequest {
  parentFolderId?: number | null;
  name?: string;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * Tipos de objeto que se pueden ELEGIR al crear un workflow.
 *
 * Sin `CUSTOM_OBJECT` ni `SUBSCRIPTION`: son tipos declarados en el contrato que no existen como
 * entidad del CRM —no hay tabla, ni servicio, ni módulo que publique sus eventos—, así que un
 * workflow creado sobre ellos se guardaba, se activaba y no se disparaba jamás. Siguen en
 * `WorkflowObjectType` porque la API los declara y la auditoría antigua puede nombrarlos; lo que
 * desaparece es la posibilidad de crear uno nuevo a ciegas.
 */
export const WORKFLOW_OBJECT_TYPES_ENABLED: WorkflowObjectType[] = [
  'CONTACT',
  'COMPANY',
  'DEAL',
  'QUOTE',
  'TICKET'
];

export const WORKFLOW_OBJECT_TYPE_LABELS: Record<WorkflowObjectType, string> = {
  CONTACT: 'Contacto',
  COMPANY: 'Empresa',
  DEAL: 'Negocio',
  QUOTE: 'Cotización',
  CUSTOM_OBJECT: 'Objeto personalizado',
  SUBSCRIPTION: 'Suscripción',
  TICKET: 'Ticket'
};

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  ARCHIVED: 'Archivado'
};

export type WorkflowRuleOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'IN'
  | 'NOT_IN';

export type ReEnrollmentStartMode = 'FROM_START' | 'FROM_NODE';

export interface WorkflowExitCriteriaRule {
  type: 'PROPERTY';
  objectType?: string;
  field: string;
  operator: WorkflowRuleOperator;
  value?: any;
  reason?: string;
  /** Valores de `IN` / `NOT_IN`. */
  values?: any[];
  /** Extremos de `BETWEEN`. */
  from?: any;
  to?: any;
}

export interface WorkflowSuppressionRule {
  type: 'OBJECT' | 'PROPERTY';
  objectType?: string;
  objectId?: number;
  field?: string;
  operator?: WorkflowRuleOperator;
  value?: any;
  /** Valores de `IN` / `NOT_IN`. El motor compara contra este array, no contra un texto con comas. */
  values?: any[];
  /** Extremos de `BETWEEN`. */
  from?: any;
  to?: any;
  reason?: string;
}

export interface WorkflowSettings {
  allowReEnrollment?: boolean;
  reEnrollmentStartMode?: ReEnrollmentStartMode;
  reEnrollmentStartNodeId?: number | null;
  exitCriteria?: WorkflowExitCriteriaRule[];
  suppressionRules?: WorkflowSuppressionRule[];
}

export const WORKFLOW_RULE_OPERATOR_LABELS: Record<WorkflowRuleOperator, string> = {
  EQUALS:        'Igual a',
  NOT_EQUALS:    'No es igual a',
  CONTAINS:      'Contiene',
  NOT_CONTAINS:  'No contiene',
  IS_EMPTY:      'Está vacío',
  IS_NOT_EMPTY:  'No está vacío',
  GREATER_THAN:  'Mayor que',
  LESS_THAN:     'Menor que',
  IN:            'En lista',
  NOT_IN:        'No en lista'
};
